import React, { useCallback, useEffect, useRef, useState } from 'react';
import { recordScore, getProgress, setLevel, unlockNext, getHighScore } from '../gamesLib';
import { sfx } from '../gameSound';

interface Props {
  onExit: () => void;
  challengeScore?: number | null;
}

const COLS = 7;
const ROWS = 6;
type Disc = 0 | 1 | 2; // 0 empty, 1 player (red), 2 AI (yellow)
type Board = Disc[][];   // [row][col], row 0 = top

const DIFFS = [
  { name: 'Easy', depth: 2 },
  { name: 'Medium', depth: 4 },
  { name: 'Hard', depth: 6 },
];

function empty(): Board {
  return Array.from({ length: ROWS }, () => Array<Disc>(COLS).fill(0));
}

function dropRow(b: Board, col: number): number {
  for (let r = ROWS - 1; r >= 0; r--) if (b[r][col] === 0) return r;
  return -1;
}

function validCols(b: Board): number[] {
  const cols: number[] = [];
  for (let c = 0; c < COLS; c++) if (b[0][c] === 0) cols.push(c);
  return cols;
}

function winnerAt(b: Board): Disc | null {
  const dirs = [[0, 1], [1, 0], [1, 1], [1, -1]];
  for (let r = 0; r < ROWS; r++) {
    for (let c = 0; c < COLS; c++) {
      const p = b[r][c];
      if (!p) continue;
      for (const [dr, dc] of dirs) {
        let k = 1;
        while (k < 4) {
          const nr = r + dr * k, nc = c + dc * k;
          if (nr < 0 || nr >= ROWS || nc < 0 || nc >= COLS || b[nr][nc] !== p) break;
          k++;
        }
        if (k === 4) return p;
      }
    }
  }
  return null;
}

// Heuristic: score windows of 4 for the AI (player 2).
function scoreWindow(cells: Disc[], me: Disc, opp: Disc): number {
  const m = cells.filter(c => c === me).length;
  const o = cells.filter(c => c === opp).length;
  const e = cells.filter(c => c === 0).length;
  if (m === 4) return 1000;
  if (m === 3 && e === 1) return 50;
  if (m === 2 && e === 2) return 10;
  if (o === 3 && e === 1) return -80;
  return 0;
}

function evaluate(b: Board, me: Disc): number {
  const opp = (me === 1 ? 2 : 1) as Disc;
  let score = 0;
  // Center preference.
  for (let r = 0; r < ROWS; r++) if (b[r][3] === me) score += 6;
  const windows: Disc[][] = [];
  for (let r = 0; r < ROWS; r++) for (let c = 0; c <= COLS - 4; c++) windows.push([b[r][c], b[r][c + 1], b[r][c + 2], b[r][c + 3]]);
  for (let c = 0; c < COLS; c++) for (let r = 0; r <= ROWS - 4; r++) windows.push([b[r][c], b[r + 1][c], b[r + 2][c], b[r + 3][c]]);
  for (let r = 0; r <= ROWS - 4; r++) for (let c = 0; c <= COLS - 4; c++) windows.push([b[r][c], b[r + 1][c + 1], b[r + 2][c + 2], b[r + 3][c + 3]]);
  for (let r = 0; r <= ROWS - 4; r++) for (let c = 3; c < COLS; c++) windows.push([b[r][c], b[r + 1][c - 1], b[r + 2][c - 2], b[r + 3][c - 3]]);
  for (const w of windows) score += scoreWindow(w, me, opp);
  return score;
}

function minimax(b: Board, depth: number, alpha: number, beta: number, maxing: boolean): [number, number] {
  const win = winnerAt(b);
  const cols = validCols(b);
  if (win === 2) return [100000 + depth, -1];
  if (win === 1) return [-100000 - depth, -1];
  if (cols.length === 0) return [0, -1];
  if (depth === 0) return [evaluate(b, 2), -1];

  let bestCol = cols[Math.floor(cols.length / 2)];
  if (maxing) {
    let value = -Infinity;
    for (const c of cols) {
      const r = dropRow(b, c);
      b[r][c] = 2;
      const [s] = minimax(b, depth - 1, alpha, beta, false);
      b[r][c] = 0;
      if (s > value) { value = s; bestCol = c; }
      alpha = Math.max(alpha, value);
      if (alpha >= beta) break;
    }
    return [value, bestCol];
  } else {
    let value = Infinity;
    for (const c of cols) {
      const r = dropRow(b, c);
      b[r][c] = 1;
      const [s] = minimax(b, depth - 1, alpha, beta, true);
      b[r][c] = 0;
      if (s < value) { value = s; bestCol = c; }
      beta = Math.min(beta, value);
      if (alpha >= beta) break;
    }
    return [value, bestCol];
  }
}

const GameConnect4: React.FC<Props> = ({ onExit, challengeScore }) => {
  const prog = getProgress('connect4');
  const [level, setLvl] = useState(prog.level || 1);
  const [board, setBoard] = useState<Board>(empty);
  const [turn, setTurn] = useState<Disc>(1);
  const [result, setResult] = useState<Disc | 'draw' | null>(null);
  const [streak, setStreak] = useState(0);
  const [best] = useState(getHighScore('connect4'));
  const [thinking, setThinking] = useState(false);
  const savedRef = useRef(0);

  const diff = DIFFS[Math.min(level - 1, DIFFS.length - 1)];

  const newRound = useCallback((keepStreak: boolean) => {
    setBoard(empty());
    setTurn(1);
    setResult(null);
    setThinking(false);
    if (!keepStreak) setStreak(0);
  }, []);

  const finish = (w: Disc | 'draw') => {
    setResult(w);
    if (w === 1) { sfx.win(); setStreak(s => s + 1); }
    else { if (w === 2) sfx.lose(); setStreak(0); }
  };

  const playCol = (col: number) => {
    if (result || turn !== 1 || thinking) return;
    const r = dropRow(board, col);
    if (r < 0) return;
    const nb = board.map(row => row.slice()) as Board;
    nb[r][col] = 1;
    setBoard(nb);
    sfx.place();
    const w = winnerAt(nb);
    if (w) { finish(w); return; }
    if (validCols(nb).length === 0) { finish('draw'); return; }
    setTurn(2);
  };

  // AI move.
  useEffect(() => {
    if (turn === 2 && !result) {
      setThinking(true);
      const id = setTimeout(() => {
        setBoard(prev => {
          const work = prev.map(row => row.slice()) as Board;
          const [, col] = minimax(work, diff.depth, -Infinity, Infinity, true);
          const c = col >= 0 ? col : validCols(work)[0];
          const r = dropRow(work, c);
          if (r >= 0) work[r][c] = 2;
          const w = winnerAt(work);
          if (w) finish(w);
          else if (validCols(work).length === 0) finish('draw');
          else setTurn(1);
          setThinking(false);
          return work;
        });
      }, 280);
      return () => clearTimeout(id);
    }
  }, [turn, result, diff.depth]);

  // Record best streak; unlock next difficulty after a win.
  useEffect(() => {
    if (streak > savedRef.current) {
      savedRef.current = streak;
      recordScore('connect4', streak, level);
      if (level < DIFFS.length) unlockNext('connect4', level);
    }
  }, [streak, level]);

  return (
    <div className="gc4">
      <div className="game-toolbar">
        <button className="game-back" onClick={onExit}>← Arcade</button>
        <div className="game-stats">
          <span className="gstat"><b>{streak}</b><i>streak</i></span>
          <span className="gstat"><b>{Math.max(best, streak)}</b><i>best</i></span>
          <span className="gstat"><b>{diff.name}</b><i>mode</i></span>
        </div>
        <button className="game-restart" onClick={() => newRound(true)}>↻ New</button>
      </div>

      <div className="g2048-levels">
        {DIFFS.map((d, i) => {
          const lvl = i + 1;
          const unlocked = lvl <= getProgress('connect4').unlockedLevel;
          return (
            <button key={d.name} className={`lvl-pill ${level === lvl ? 'active' : ''} ${unlocked ? '' : 'locked'}`}
              onClick={() => { if (unlocked) { setLvl(lvl); setLevel('connect4', lvl); newRound(false); } }}
              disabled={!unlocked} title={unlocked ? d.name : 'Win to unlock'}>
              {unlocked ? d.name : '🔒'}
            </button>
          );
        })}
      </div>

      {challengeScore != null && (
        <div className="challenge-target">🎯 Beat the challenge: <b>{challengeScore}</b> win streak</div>
      )}

      <div className="gc4-board">
        {Array.from({ length: COLS }, (_, c) => (
          <button key={c} className="gc4-col" onClick={() => playCol(c)} disabled={!!result || turn !== 1 || thinking}>
            {Array.from({ length: ROWS }, (_, r) => (
              <span key={r} className={`gc4-slot ${board[r][c] === 1 ? 'p1' : board[r][c] === 2 ? 'p2' : ''}`} />
            ))}
          </button>
        ))}
      </div>

      <div className="gttt-status">
        {result === 1 && <span className="win">You win! 🔴 Streak {streak}</span>}
        {result === 2 && <span className="lose">Computer wins 🟡 — streak reset</span>}
        {result === 'draw' && <span className="draw">Draw 🤝 — streak reset</span>}
        {!result && <span>{turn === 1 ? 'Drop a disc (you are 🔴)' : 'Computer thinking…'}</span>}
      </div>

      {result && (
        <div className="go-actions" style={{ marginTop: 12 }}>
          <button className="g-btn primary" onClick={() => newRound(true)}>Play again</button>
          <button className="g-btn ghost" onClick={onExit}>Back to arcade</button>
        </div>
      )}

      <p className="game-hint">Click a column to drop. Connect four in a row to win and build a streak.</p>
    </div>
  );
};

export default GameConnect4;
